import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Check, ChevronsUpDown, Loader2, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useCustomers, type Customer } from '@/features/customers/api';

export interface BuyerRef {
  id: number;
  label: string;
}

/**
 * Picks the invoice buyer from the user's saved customers. A popover with a debounced search over
 * `useCustomers`; selecting one links it to the draft (the API stores the id and denormalizes the
 * label). The customer list query lives inside the popover content, so it only fetches when opened.
 */
export function BuyerPicker({
  value,
  onSelect,
  disabled,
  invalid,
  trigger,
  align = 'start',
  contentClassName = 'w-(--radix-popover-trigger-width)',
}: {
  value: BuyerRef | null;
  onSelect: (buyer: BuyerRef | null) => void;
  disabled?: boolean;
  invalid?: boolean;
  /** Custom popover trigger (e.g. a compact "Change" button); defaults to the full-width field. */
  trigger?: ReactNode;
  align?: 'start' | 'end';
  /** Popover width; defaults to matching the trigger. Give a fixed width for a compact trigger. */
  contentClassName?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            aria-invalid={invalid || undefined}
            className="w-full justify-between font-normal"
          >
            <span className={cn('truncate', !value && 'text-muted-foreground')}>
              {value ? value.label : t('invoices.compose.buyer.select')}
            </span>
            <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align={align} className={cn(contentClassName, 'p-0')}>
        <BuyerSearchList
          selectedId={value?.id ?? null}
          onSelect={(buyer) => {
            onSelect(buyer);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

function BuyerSearchList({
  selectedId,
  onSelect,
}: {
  selectedId: number | null;
  onSelect: (buyer: BuyerRef | null) => void;
}) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setQuery(input), 300);
    return () => clearTimeout(id);
  }, [input]);

  const customers = useCustomers(query);
  const items = useMemo(() => {
    const seen = new Set<number>();
    const flat: Customer[] = [];
    for (const page of customers.data?.pages ?? []) {
      for (const customer of page.items) {
        if (!seen.has(customer.id)) {
          seen.add(customer.id);
          flat.push(customer);
        }
      }
    }
    return flat;
  }, [customers.data]);

  return (
    <div className="flex max-h-80 flex-col">
      <div className="border-b p-2">
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('invoices.compose.buyer.search')}
            className="pl-8"
            autoComplete="off"
            autoFocus
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-1">
        {selectedId != null ? (
          <button
            type="button"
            onClick={() => onSelect(null)}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground outline-none hover:bg-muted focus-visible:bg-muted"
          >
            <X className="size-4" />
            {t('invoices.compose.buyer.clear')}
          </button>
        ) : null}
        {customers.isPending ? (
          <div className="grid place-items-center py-6">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </div>
        ) : customers.isError ? (
          <p className="px-2 py-6 text-center text-sm text-destructive">
            {t('invoices.compose.buyer.load_error')}
          </p>
        ) : items.length === 0 ? (
          <div className="space-y-2 px-2 py-6 text-center">
            <p className="text-sm text-muted-foreground">{t('invoices.compose.buyer.empty')}</p>
            <Button asChild variant="link" size="sm">
              <Link to="/customers">{t('invoices.compose.buyer.add_customer')}</Link>
            </Button>
          </div>
        ) : (
          items.map((customer) => {
            const active = customer.id === selectedId;
            const subtitle = [customer.nip, customer.address.postTown].filter(Boolean).join(' · ');
            return (
              <button
                key={customer.id}
                type="button"
                onClick={() => onSelect({ id: customer.id, label: customer.name })}
                aria-current={active ? 'true' : undefined}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left outline-none hover:bg-muted focus-visible:bg-muted',
                  active && 'bg-muted',
                )}
              >
                <Check className={cn('size-4 shrink-0', active ? 'opacity-100' : 'opacity-0')} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{customer.name}</span>
                  {subtitle !== '' ? (
                    <span className="block truncate text-xs text-muted-foreground">{subtitle}</span>
                  ) : null}
                </span>
              </button>
            );
          })
        )}
        {customers.isFetchingNextPage ? (
          <div className="py-2 text-center text-xs text-muted-foreground">
            {t('invoices.drafts.loading_more')}
          </div>
        ) : null}
      </div>
    </div>
  );
}
